import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const DISMISSED = 'doseg.install.dismissed'

/** Already an app on this device: nothing to offer. */
function installed() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  )
}

/** iPhone and iPad, where Safari has no install event and the owner does it by hand. */
function isApple() {
  const ua = navigator.userAgent

  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/**
 * An offer to keep Dosegni as an app rather than a tab.
 *
 * It is the difference between a shop owner who checks their posts and one who does not: a tab is
 * something you have to remember to open. Shown on the way in and inside the app, because the way
 * in is where most people are the first time and inside is where they are every time after.
 *
 * Never a nag. It appears only when the browser says the app can actually be installed, it is
 * gone the moment it is, and dismissing it is remembered. On an iPhone there is no such event and
 * no button that can do it, so it says what to tap instead of pretending.
 */
export default function InstallPrompt() {
  const { t } = useTranslation()
  const [event, setEvent] = useState(null)
  const [apple, setApple] = useState(false)
  const [gone, setGone] = useState(() => localStorage.getItem(DISMISSED) === '1' || installed())

  useEffect(() => {
    if (gone) return undefined

    function offered(browserEvent) {
      // The browser's own banner is suppressed by this; the offer here is the one that appears.
      browserEvent.preventDefault()
      setEvent(browserEvent)
    }

    function done() {
      setGone(true)
      setEvent(null)
    }

    window.addEventListener('beforeinstallprompt', offered)
    window.addEventListener('appinstalled', done)

    // Safari never fires the event and never will; on an iPhone the offer is instructions.
    if (isApple() && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS/.test(navigator.userAgent)) {
      setApple(true)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', offered)
      window.removeEventListener('appinstalled', done)
    }
  }, [gone])

  if (gone || (!event && !apple)) return null

  const dismiss = () => {
    localStorage.setItem(DISMISSED, '1')
    setGone(true)
  }

  return (
    <div className={`install${apple ? ' install--apple' : ''}`}>
      <img className="install__mark" src="/icon-192.png" alt="" />

      <span className="install__text">
        <span className="install__title">{t('install.title')}</span>
        <span className="muted small install__help">{apple ? t('install.apple') : t('install.help')}</span>
      </span>

      {event && (
        <button
          type="button"
          className="button"
          onClick={async () => {
            // One shot: a prompt that has been answered cannot be shown again.
            const answered = event.prompt()
            setEvent(null)

            try {
              await answered
            } finally {
              setGone(true)
            }
          }}
        >
          {t('install.action')}
        </button>
      )}

      <button type="button" className="install__close" aria-label={t('common.close')} onClick={dismiss}>
        ✕
      </button>
    </div>
  )
}
